'use client';
import { useContext } from 'react';
import { toast } from 'react-hot-toast';
import { Modal, Spinner } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';
import { useWatchlistContext } from '@benzinga/user-context';
import { Watchlist, WatchlistManager } from '@benzinga/watchlist-manager';
import { PortfolioModalHeader } from './PortfolioModalHeader';
import {
  portfolioModalBodyClass,
  portfolioModalContainerStyle,
  portfolioModalFooterClass,
  portfolioModalShellClass,
} from './modalStyles';

export const DeleteWatchlist = () => {
  const { deleteModal, setDeleteModal } = useWatchlistContext();
  const session = useContext(SessionContext);
  const watchlistManager = session.getManager(WatchlistManager);

  const handleDeleteWatchlist = async (watchlist: Watchlist | null) => {
    if (!watchlist) return;
    setDeleteModal({ show: true, watchlist: watchlist, isLoading: true });
    try {
      const result = await watchlistManager.removeWatchlist(watchlist);
      if (result.err) {
        toast.error('Unable to delete watchlist. Please try again.');
        return;
      }
      toast.success(`${watchlist.name} deleted`);
    } catch (error) {
      console.error('Error deleting watchlist:', error);
      toast.error('Error deleting watchlist. Please try again.');
    } finally {
      setDeleteModal({ show: false, watchlist: null, isLoading: false });
    }
  };

  const cancelDeleteWatchlist = () => {
    setDeleteModal({ show: false, watchlist: null, isLoading: false });
  };

  return (
    <Modal
      visible={deleteModal.show}
      onClose={cancelDeleteWatchlist}
      disableFullScreenButton={true}
      hideTitle={true}
      fullScreen={true}
      size={{ manual: { width: '440px', maxWidth: 'calc(100vw - 32px)', height: 'auto' } }}
      containerStyle={portfolioModalContainerStyle}
    >
      <div className={portfolioModalShellClass}>
        <PortfolioModalHeader title="Delete watchlist" onClose={cancelDeleteWatchlist} />
        <div className={portfolioModalBodyClass}>
          <p className="text-sm leading-6 text-slate-600">
            Delete <strong className="text-slate-950">{deleteModal.watchlist?.name}</strong>? This removes the watchlist
            and its alert settings. This cannot be undone.
          </p>
        </div>
        <div className={portfolioModalFooterClass}>
          <button
            type="button"
            onClick={cancelDeleteWatchlist}
            disabled={deleteModal.isLoading}
            className="min-h-10 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleDeleteWatchlist(deleteModal.watchlist)}
            disabled={deleteModal.isLoading}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-red-600 px-4 text-sm font-bold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {deleteModal.isLoading ? <Spinner size={16} /> : 'Delete watchlist'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
